import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

/**
 * Staff Selection Modal Component
 *
 * Modal interface for selecting multiple staff members with:
 * - Real-time search (firstName, lastName, email)
 * - Selected/Available sections
 * - Initial letter avatars
 * - Email display
 * - Count badges
 * - Bulk actions (Select All, Clear All)
 */
@Component({
  selector: 'app-staff-selection-modal',
  standalone: false,
  templateUrl: './staff-selection-modal.component.html',
  styleUrls: ['./staff-selection-modal.component.scss'],
})
export class StaffSelectionModalComponent implements OnInit {

  @Input() allStaff: any[] = [];
  @Input() selectedStaffIds: number[] = [];
  @Input() fieldLabel: string = 'Staff';

  filteredStaff: any[] = [];
  searchTerm: string = '';
  selectedIds: Set<number> = new Set();

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    // Initialize selected IDs
    this.selectedStaffIds.forEach(id => this.selectedIds.add(Number(id)));

    // Initialize filtered list
    this.filteredStaff = [...this.allStaff];
  }

  /**
   * Filter staff based on search term
   * Searches in: firstName, lastName, email
   */
  filterStaff(): void {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredStaff = [...this.allStaff];
      return;
    }

    this.filteredStaff = this.allStaff.filter(staff => {
      const firstName = staff.firstName?.toLowerCase() || '';
      const lastName = staff.lastName?.toLowerCase() || '';
      const email = staff.email?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();

      return firstName.includes(term) ||
             lastName.includes(term) ||
             fullName.includes(term) ||
             email.includes(term);
    });
  }

  /**
   * Toggle staff selection
   */
  toggleStaff(staff: any, event?: any): void {
    const staffId = Number(staff.id);

    if (this.selectedIds.has(staffId)) {
      this.selectedIds.delete(staffId);
    } else {
      this.selectedIds.add(staffId);
    }

    // Prevent checkbox default behavior if event exists
    if (event) {
      event.preventDefault();
    }
  }

  /**
   * Check if staff is selected
   */
  isSelected(staff: any): boolean {
    return this.selectedIds.has(Number(staff.id));
  }

  /**
   * Get selected staff objects
   */
  getSelectedStaff(): any[] {
    return this.allStaff
      .filter(staff => this.selectedIds.has(Number(staff.id)))
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }

  /**
   * Get available (unselected) staff from filtered list
   */
  getAvailableStaff(): any[] {
    return this.filteredStaff
      .filter(staff => !this.selectedIds.has(Number(staff.id)))
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }

  /**
   * Get staff initials for avatar
   */
  getStaffInitials(staff: any): string {
    const first = staff.firstName?.charAt(0)?.toUpperCase() || '';
    const last = staff.lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  }

  /**
   * Get full staff name
   */
  getStaffFullName(staff: any): string {
    return `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
  }

  /**
   * Highlight matching text in search results
   */
  highlightMatch(text: string): string {
    if (!this.searchTerm || !text) {
      return text;
    }

    const term = this.searchTerm.trim();
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  /**
   * Select all filtered staff
   */
  selectAll(): void {
    this.filteredStaff.forEach(staff => {
      this.selectedIds.add(Number(staff.id));
    });
  }

  /**
   * Clear all selections
   */
  clearAll(): void {
    this.selectedIds.clear();
  }

  /**
   * Confirm selection and close modal
   */
  confirm(): void {
    this.modalController.dismiss({
      selectedIds: Array.from(this.selectedIds)
    }, 'confirm');
  }

  /**
   * Cancel and close modal
   */
  dismiss(): void {
    this.modalController.dismiss(null, 'cancel');
  }

  // Computed properties for template
  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get availableCount(): number {
    return this.getAvailableStaff().length;
  }

  get hasFilteredResults(): boolean {
    return this.filteredStaff.length > 0;
  }

  get hasNoResults(): boolean {
    return this.searchTerm.trim() !== '' && this.filteredStaff.length === 0;
  }
}
